import "react-app-polyfill/ie11";
import "react-app-polyfill/stable";
import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import App from "./App";
import "./assets/scss/style.scss";
import * as serviceWorker from "./serviceWorker";
import { setAuthToken, configureBaseUrl, setAuthInterceptor } from "./util/auth/sessionAPIUtil";
import { restoreToken, getAccessToken, getSession } from "./util/auth/tokenStorage";
import { removeAuthParamsFromCurrentUrl } from "./util/auth/authUrl";
import configureStore from "./redux/configureStore/configureStore";
import { refreshToken } from "./redux/actions/sessionActions";
import { initState } from "./redux/reducers/artworkReducer";
import { initState as initStatequote } from "./redux/reducers/quoteReducer";
import { initState as initStatedashboard } from "./redux/reducers/DashboardReducer";
import { initState as initStatecatalogue } from "./redux/reducers/catalogueReducer";
import { initState as initStateMcatalogue } from "./redux/reducers/mcatalogueReducer";
import * as currencyActions from "./redux/actions/currencyActions";
import * as sessionActions from "./redux/actions/sessionActions";
import * as orderActions from "./redux/actions/ordersActions";
import { migrateStoredCart } from "./util/cart/cartStorage";

const getCartdatafromlocalstorage = () => {
  // Shrinks carts left behind by older builds, which stored the whole product detail once
  // per row and could sit over the localStorage quota. Never throws: a corrupt or oversized
  // cart must not stop the app from booting.
  try {
    const cart = migrateStoredCart();
    cart.forEach(item => {
      if (item && item.currentOrder && item.currentOrder.size) {
        try {
          item.currentOrder.size = JSON.parse(item.currentOrder.size);
        } catch (e) { /* legacy field, leave as-is */ }
      }
    });
    return cart;
  } catch (e) {
    return [];
  }
}
document.addEventListener("DOMContentLoaded", async () => {
  let store;
  // Store used for the URL-credential exchange. Kept so that, if the exchange
  // fails, we render the store its error was dispatched into instead of a
  // fresh empty one — the credential has already been stripped from the URL,
  // so a silent login page would leave the user with no way to retry.
  let tokenExchangeStore = null;
  if (process.env.NODE_ENV === 'production') {
    const baseUrl = "/protected";
    configureBaseUrl(baseUrl);
  }
  // Decrypt the stored token into memory BEFORE anything below can dispatch an
  // action that issues a request — getSession() returns null until this
  // resolves, so a request racing startup would go out unauthenticated.
  await restoreToken();
  // Install the interceptor only after token restoration has settled. This
  // makes the boot gate apply to Axios itself as well as Redux/React: even if
  // a future bootstrap step starts a request, the interceptor can never
  // observe the pre-hydration in-memory token cache.
  if (process.env.NODE_ENV === 'production') {
    setAuthInterceptor();
  }
  const query = new URLSearchParams(window.location.search);
  const token = query.get('cp-token')
  const contactId = query.get('contactId')
  if (token) {
    // Strip the credential immediately after capturing it. This does not wait
    // for the exchange request, so API failures cannot leave it in history.
    removeAuthParamsFromCurrentUrl();
    tokenExchangeStore = configureStore({});
    const exchanged = await tokenExchangeStore.dispatch(sessionActions.loginByToken({
      "cp-token": token,
      'contactId': contactId
    }));
    // On success loginByToken removes the credential from the URL and reloads.
    // Do not render components while the exchange is pending: several legacy
    // components otherwise copy the one-time credential into more request URLs.
    if (exchanged) return;
    // Preview routes are intentionally public, so an unauthenticated failed
    // exchange would otherwise render a blank page with session errors nobody
    // displays. Keep an already-authenticated user on the requested route;
    // the stale one-time credential has already been removed from its URL.
    if (!getAccessToken()) {
      window.history.replaceState({}, document.title, '/cp/login');
    }
  }
  const decodedUser = getSession();
  if (getAccessToken()) {
    setAuthToken(decodedUser.token);
    const preloadedState = {
      cartData: getCartdatafromlocalstorage(),
      session: { isAuthenticated: true, user: decodedUser },
      entities: {
        orders: {
          loading: false,
          ordersList: [],
          selectedOrderList: [],
          fetchError: false,
          filters: {
            orderNo: "",
            orderStatus: decodedUser && decodedUser.portalPermissions && decodedUser.portalPermissions.Order[0].allowedStatus,
            paymentStatus: ["Paid", "Unpaid", "Partial"],
            accountName: "",
            orderIdentity: "",
            mainSearch: "",
            account_id: decodedUser.accountId,
            contact_id:
              (decodedUser.portalPermissions && !decodedUser.portalPermissions.Order[0].allowedOtherContacts)
                ? decodedUser.contactId
                : "",
            source: "cp",
            cp_user_id: decodedUser.contactId,
          },
          statuses: decodedUser && decodedUser.portalPermissions && decodedUser.portalPermissions.Order[0].allowedStatus,
          paymentStatuses: ["Paid", "Unpaid", "Partial"],
          page: 0,
          count: 1,
          rowsPerPage: 50,
          orderConfirmations: {},
          artworkDocuments: {},
          invoiceDocuments: {},
          cleared: 0,
          paymentData: {
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            address: "",
            city: "",
            state: "",
            zipCode: "",
            country: "USA",
            amount: "",
            accttype: "",
            cardNumber: "",
            expDate: "",
            expMonth: "",
            expYear: "",
            cvv: "",
            mytoken: "",
            orders: [],
            paymentAmount: "0"
          }
        },
        artwork: {
          ...initState,
          filters: {
            ...initState.filters,
            account_id: decodedUser.accountId,
            statusName: decodedUser.portalPermissions && decodedUser.portalPermissions.Artwork[0].allowedStatus,
            contact_id:
              (decodedUser.portalPermissions && !decodedUser.portalPermissions.Artwork[0].allowedOtherContacts)
                ? decodedUser.contactId
                : "",
          },
          statuses: decodedUser.portalPermissions && decodedUser.portalPermissions.Artwork[0].allowedStatus,
        },
        quote: {
          ...initStatequote,
          quotes: [],
          statuses: decodedUser.portalPermissions && decodedUser.portalPermissions.Quote[0].allowedStatus,
          filters: {
            ...initStatequote.filters,
            orderStatus: decodedUser.portalPermissions && decodedUser.portalPermissions.Quote[0].allowedStatus,
            account_id: decodedUser.accountId,
            contact_id:
              (decodedUser.portalPermissions && !decodedUser.portalPermissions.Quote[0].allowedOtherContacts)
                ? decodedUser.contactId
                : "",
            cp_user_id: decodedUser.contactId,
            saveBtn: false,
            changeOnItems: [],
            selectedItems: [],
            decoratorsTypeList:[],
            orderNo: ""
          },
          cleared: 0,
        },
        dashboard: {
          ...initStatedashboard,
          statuses: decodedUser.portalPermissions && decodedUser.portalPermissions.Quote[0].allowedStatus,
          filters: {
            ...initStatequote.filters,
            orderStatus: decodedUser.portalPermissions && decodedUser.portalPermissions.Quote[0].allowedStatus,
            account_id: decodedUser.accountId,
            contact_id:
              (decodedUser.portalPermissions && !decodedUser.portalPermissions.Quote[0].allowedOtherContacts)
                ? decodedUser.contactId
                : "",
            cp_user_id: decodedUser.contactId,
          },
          cleared: 0,
        },
        catalogue: {
          ...initStatecatalogue,
          catalogues: [],
          categories: [],
          filters: {
            ...initStatequote.filters,
            account_id: decodedUser.accountId,
            contact_id:
              (decodedUser.portalPermissions && !decodedUser.portalPermissions.Catalogue[0].allowedOtherContacts)
                ? decodedUser.contactId
                : "",
            source: "cp",
            cp_user_id: decodedUser.contactId,
          },
          cleared: 0,
        },
        mcatalogue: {
          ...initStateMcatalogue,
          catalogues: [],
          categories: [],
          filters: {
            ...initStatequote.filters,
            account_id: decodedUser.accountId,
            contact_id:
              (decodedUser.portalPermissions && !decodedUser.portalPermissions.Catalogue[0].allowedOtherContacts)
                ? decodedUser.contactId
                : "",
            source: "cp",
            cp_user_id: decodedUser.contactId,
          },
          cleared: 0,
        },
        cart:{
          loading:false,
          artworkList:{},
        }
      },
    };
    store = configureStore(preloadedState);
    store.dispatch(currencyActions.setUserCurrency(decodedUser));
    const currentTime = Date.now() / 1000;
    const sessionReady = decodedUser.exp < currentTime
      ? await store.dispatch(refreshToken())
      : true;
    // These boot requests go through the auth interceptor, which now rejects
    // (rather than resolving) when a session refresh fails. Swallow the
    // rejection here: the interceptor has already dispatched logout, and an
    // unhandled rejection is all that would be left to report.
    if (sessionReady) {
      Promise.resolve(store.dispatch(orderActions.fetchCurrencyExchangeRates())).catch(() => {
        console.error('Customer Portal could not load currency exchange rates.');
      });
      Promise.resolve(store.dispatch(sessionActions.getAdditionalConfig())).catch(() => {
        console.error('Customer Portal could not load additional field configuration.');
      });
    }
  } else {
    store = tokenExchangeStore || configureStore({});
  }


  // fetch products from json file
  //store.dispatch(fetchProducts(products));

  ReactDOM.render(
    <Provider store={store}>
      <App />
    </Provider>,
    document.getElementById("root")
  );
});

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
